import { useEffect, useRef } from 'react';
import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import {
  useTropicalDemoKind,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import type { ArcgisMapView } from '../utils/arcgisView';
import { symbolToPictureMarkerMap } from '../utils/uscgSymbology';
import { arcGisGetOrCreatePopupTemplate } from '../arcgis-integration/arcGisGetOrCreatePopupTemplate';
import {
  CFA_HELENE_STORES,
  CFA_HELENE_STORES_LAYER_ID,
  CFA_HELENE_STORES_LAYER_TITLE,
  EPA_GULF_FACILITIES_LAYER_ID,
  HISTORICAL_HURRICANES_LAYER_ID,
} from './historicalHurricanesHeleneData';
import {
  EPA_ERMA_HELENE_LAYER_IDS,
  EPA_ERMA_HELENE_LAYERS,
  EPA_ERMA_NPL_LAYER_ID,
  EPA_ERMA_NPL_LAYER_TITLE,
  EPA_NPL_SUPERFUND_FEATURE_SERVER_URL,
} from './epaHeleneErmaLayers';
import {
  nplSiteDefinitionExpression,
  queryNplSitesInHeleneCone,
  type EpaNplSite,
} from './queryNplSitesInHeleneCone';
import {
  DHS_REGION4_ASSETS_LAYER_ID,
  DHS_REGION4_ASSETS_LAYER_TITLE,
  DHS_REGION4_FACILITIES,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/dhsRegion4ThreatNotification';
import {
  ONTARIO_GTA_ASSETS_LAYER_ID,
  ONTARIO_GTA_ASSETS_LAYER_TITLE,
  ONTARIO_GTA_FACILITIES,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/ontarioSnowstormNotification';
import {
  CNIC_HAWAII_ASSETS_LAYER_ID,
  CNIC_HAWAII_ASSETS_LAYER_TITLE,
  CNIC_HAWAII_FACILITIES,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/cnicHawaiiThreatNotification';
import {
  assetsInCone,
  USCG_FOC_SE_ASSETS_LAYER_ID,
  USCG_FOC_SE_ASSETS_LAYER_TITLE,
} from '../foc-demo/uscgFocSoutheastTropicalData';
import { buildHeleneResourcePopupTemplate } from './heleneResourcePopup';
const DHS_ASSET_POPUP = new PopupTemplate({
  title: '{name}',
  content: [
    {
      type: 'fields',
      fieldInfos: [
        { fieldName: 'city', label: 'City' },
        { fieldName: 'state', label: 'State' },
      ],
    },
  ],
});

const STORE_POPUP = new PopupTemplate({
  title: '{name}',
  content: [
    {
      type: 'fields',
      fieldInfos: [
        { fieldName: 'storeNumber', label: 'Store #' },
        { fieldName: 'city', label: 'City' },
        { fieldName: 'state', label: 'State' },
        { fieldName: 'inConeLabel', label: 'In forecast cone' },
      ],
    },
  ],
});

function buildOntarioAssetGraphics(): Graphic[] {
  const facility = symbolToPictureMarkerMap.facility;
  return ONTARIO_GTA_FACILITIES.map(
    (asset) =>
      new Graphic({
        geometry: new Point({
          longitude: asset.lon,
          latitude: asset.lat,
        }),
        symbol: facility,
        attributes: {
          id: asset.id,
          name: asset.name,
          city: asset.city,
          state: asset.state,
        },
        popupTemplate: DHS_ASSET_POPUP,
      })
  );
}

function buildCnicAssetGraphics(): Graphic[] {
  const facility = symbolToPictureMarkerMap.facility;
  return CNIC_HAWAII_FACILITIES.map(
    (asset) =>
      new Graphic({
        geometry: new Point({
          longitude: asset.lon,
          latitude: asset.lat,
        }),
        symbol: facility,
        attributes: {
          id: asset.id,
          name: asset.name,
          city: asset.city,
          state: asset.state,
        },
        popupTemplate: DHS_ASSET_POPUP,
      })
  );
}

function buildDhsAssetGraphics(): Graphic[] {
  const facility = symbolToPictureMarkerMap.facility;
  return DHS_REGION4_FACILITIES.map(
    (asset) =>
      new Graphic({
        geometry: new Point({
          longitude: asset.lon,
          latitude: asset.lat,
        }),
        symbol: facility,
        attributes: {
          id: asset.id,
          name: asset.name,
          city: asset.city,
          state: asset.state,
        },
        popupTemplate: DHS_ASSET_POPUP,
      })
  );
}

function buildUscgFocSeAssetGraphics(): Graphic[] {
  const facility = symbolToPictureMarkerMap.facility;
  const popupTemplate = buildHeleneResourcePopupTemplate();
  return assetsInCone().map(
    (asset) =>
      new Graphic({
        geometry: new Point({
          longitude: asset.lon,
          latitude: asset.lat,
        }),
        symbol: facility,
        attributes: {
          id: asset.id,
          name: `${asset.name} (${asset.city}, ${asset.state})`,
          city: asset.city,
          state: asset.state,
          type: asset.unitType,
          inConeLabel: 'Yes',
        },
        popupTemplate,
      })
  );
}

function buildStoreGraphics(): Graphic[] {
  const chickfila = symbolToPictureMarkerMap.chickfilaLocation;
  return CFA_HELENE_STORES.map(
    (store) =>
      new Graphic({
        geometry: new Point({
          longitude: store.lon,
          latitude: store.lat,
        }),
        symbol: chickfila,
        attributes: {
          id: store.id,
          name: store.name,
          storeNumber: store.storeNumber,
          city: store.city,
          state: store.state,
          inCone: store.inCone,
          inConeLabel: store.inCone ? 'Yes' : 'No',
        },
        popupTemplate: STORE_POPUP,
      })
  );
}

function ensureLayer(
  mapView: ArcgisMapView,
  id: string,
  title: string
): GraphicsLayer {
  const existing = mapView.map?.findLayerById(id) as GraphicsLayer | undefined;
  if (existing) return existing;
  const layer = new GraphicsLayer({ id, title, listMode: 'show' });
  mapView.map?.add(layer);
  return layer;
}

function removeLayerById(mapView: ArcgisMapView, id: string) {
  const layer = mapView.map?.findLayerById(id);
  if (layer) mapView.map?.remove(layer);
}

function removeEpaErmaHeleneLayers(mapView: ArcgisMapView) {
  for (const id of EPA_ERMA_HELENE_LAYER_IDS) {
    removeLayerById(mapView, id);
  }
  removeLayerById(mapView, EPA_ERMA_NPL_LAYER_ID);
}

function enableServicePopup(layer: FeatureLayer) {
  void arcGisGetOrCreatePopupTemplate([layer]).catch((error: unknown) => {
    console.warn(
      `[EPA Helene ERMA] Failed to enable popup for ${layer.title}.`,
      error
    );
  });
}

function failClosedOnLoad(
  mapView: ArcgisMapView,
  layer: FeatureLayer,
  title: string
) {
  layer.load().then(
    () => {
      enableServicePopup(layer);
    },
    (error: unknown) => {
      console.warn(
        `[EPA Helene ERMA] Failed to load ${title}; leaving it off the map.`,
        error
      );
      if (mapView.map?.findLayerById(layer.id)) {
        mapView.map.remove(layer);
      }
    }
  );
}

function ensureEpaErmaHeleneLayers(mapView: ArcgisMapView) {
  if (!mapView.map) return;
  for (const spec of EPA_ERMA_HELENE_LAYERS) {
    const existing = mapView.map.findLayerById(spec.id);
    if (existing instanceof FeatureLayer) {
      enableServicePopup(existing);
      continue;
    }
    const layer = new FeatureLayer({
      id: spec.id,
      title: spec.title,
      url: spec.url,
      listMode: 'show',
      visible: true,
      popupEnabled: true,
    });
    failClosedOnLoad(mapView, layer, spec.title);
    mapView.map.add(layer);
  }
}

function ensureEpaNplSuperfundLayer(
  mapView: ArcgisMapView,
  sites: EpaNplSite[]
) {
  if (!mapView.map) return;
  const nplSites = sites.filter((site) => site.source === 'npl');
  if (nplSites.length === 0) {
    removeLayerById(mapView, EPA_ERMA_NPL_LAYER_ID);
    return;
  }

  const definitionExpression = nplSiteDefinitionExpression(nplSites);
  const existing = mapView.map.findLayerById(EPA_ERMA_NPL_LAYER_ID);
  if (existing instanceof FeatureLayer) {
    existing.definitionExpression = definitionExpression;
    enableServicePopup(existing);
    return;
  }

  const layer = new FeatureLayer({
    id: EPA_ERMA_NPL_LAYER_ID,
    title: EPA_ERMA_NPL_LAYER_TITLE,
    url: EPA_NPL_SUPERFUND_FEATURE_SERVER_URL,
    definitionExpression,
    listMode: 'show',
    visible: true,
    popupEnabled: true,
  });
  failClosedOnLoad(mapView, layer, EPA_ERMA_NPL_LAYER_TITLE);
  mapView.map.add(layer);
}

/**
 * CFA: live Helene ERMA/NHC FeatureLayers + store pins. EPA: same Helene ERMA
 * layers + in-cone NPL Superfund FeatureLayer. DHS: same Helene ERMA layers +
 * hardcoded Region 4 asset pins (no NPL). CNIC: no Helene layers; Hawaii
 * installation pins plus a synthetic tropical-storm overlay. Ontario: no Helene
 * layers; GTA asset pins. Equinor: no Helene layers at all, a synthetic North
 * Sea slick plus Grane asset pins. Japan: likewise no Helene layers; its plume,
 * intensity and asset pins come from the Tokyo Bay incident layers. FOC (no
 * tropical demo kind): same Helene ERMA layers as CFA, without CFA store pins.
 * Removed when all demo flags are off.
 */
export function useEnsureHistoricalHurricanesDemo({
  mapView,
}: {
  mapView: ArcgisMapView | null;
}) {
  const demoKind = useTropicalDemoKind();
  const focEnabled = useUscgFocRequirementsFlag();
  const seededRef = useRef(false);

  useEffect(() => {
    if (!mapView?.map) return;

    if (!demoKind) {
      removeLayerById(mapView, HISTORICAL_HURRICANES_LAYER_ID);
      removeLayerById(mapView, CFA_HELENE_STORES_LAYER_ID);
      removeLayerById(mapView, EPA_GULF_FACILITIES_LAYER_ID);
      removeLayerById(mapView, DHS_REGION4_ASSETS_LAYER_ID);
      removeLayerById(mapView, ONTARIO_GTA_ASSETS_LAYER_ID);
      removeLayerById(mapView, CNIC_HAWAII_ASSETS_LAYER_ID);
      removeLayerById(mapView, EPA_ERMA_NPL_LAYER_ID);
      // FOC Southeast: CFA Helene ERMA polygons + FOC asset pins (no CFA stores).
      if (focEnabled) {
        ensureEpaErmaHeleneLayers(mapView);
        const focLayer = ensureLayer(
          mapView,
          USCG_FOC_SE_ASSETS_LAYER_ID,
          USCG_FOC_SE_ASSETS_LAYER_TITLE
        );
        focLayer.removeAll();
        focLayer.addMany(buildUscgFocSeAssetGraphics());
        seededRef.current = true;
      } else {
        removeEpaErmaHeleneLayers(mapView);
        removeLayerById(mapView, USCG_FOC_SE_ASSETS_LAYER_ID);
        seededRef.current = false;
      }
      return;
    }

    let cancelled = false;
    removeLayerById(mapView, USCG_FOC_SE_ASSETS_LAYER_ID);

    if (demoKind === 'ontario') {
      removeLayerById(mapView, HISTORICAL_HURRICANES_LAYER_ID);
      removeLayerById(mapView, CFA_HELENE_STORES_LAYER_ID);
      removeLayerById(mapView, EPA_GULF_FACILITIES_LAYER_ID);
      removeLayerById(mapView, DHS_REGION4_ASSETS_LAYER_ID);
      removeLayerById(mapView, CNIC_HAWAII_ASSETS_LAYER_ID);
      removeLayerById(mapView, EPA_ERMA_NPL_LAYER_ID);
      removeEpaErmaHeleneLayers(mapView);

      const ontarioLayer = ensureLayer(
        mapView,
        ONTARIO_GTA_ASSETS_LAYER_ID,
        ONTARIO_GTA_ASSETS_LAYER_TITLE
      );
      ontarioLayer.removeAll();
      ontarioLayer.addMany(buildOntarioAssetGraphics());
    } else if (demoKind === 'equinor' || demoKind === 'japan') {
      removeLayerById(mapView, HISTORICAL_HURRICANES_LAYER_ID);
      removeLayerById(mapView, CFA_HELENE_STORES_LAYER_ID);
      removeLayerById(mapView, EPA_GULF_FACILITIES_LAYER_ID);
      removeLayerById(mapView, DHS_REGION4_ASSETS_LAYER_ID);
      removeLayerById(mapView, ONTARIO_GTA_ASSETS_LAYER_ID);
      removeLayerById(mapView, CNIC_HAWAII_ASSETS_LAYER_ID);
      removeLayerById(mapView, EPA_ERMA_NPL_LAYER_ID);
      // The other demos all sit under Helene. These are in the North Sea and
      // Tokyo Bay, so the storm track and wind swath would render off-scenario.
      removeEpaErmaHeleneLayers(mapView);

      // The hazard polygons and the asset pins are seeded as internal layers on
      // the demo incident, so the layer sync draws them on both Hub and the
      // workspace. Drawing them here too would double them up on Hub.
    } else if (demoKind === 'epa') {
      removeLayerById(mapView, HISTORICAL_HURRICANES_LAYER_ID);
      removeLayerById(mapView, CFA_HELENE_STORES_LAYER_ID);
      removeLayerById(mapView, EPA_GULF_FACILITIES_LAYER_ID);
      removeLayerById(mapView, DHS_REGION4_ASSETS_LAYER_ID);
      removeLayerById(mapView, ONTARIO_GTA_ASSETS_LAYER_ID);
      removeLayerById(mapView, CNIC_HAWAII_ASSETS_LAYER_ID);
      ensureEpaErmaHeleneLayers(mapView);

      void queryNplSitesInHeleneCone()
        .then((sites) => {
          if (cancelled || !mapView.map) return;
          ensureEpaNplSuperfundLayer(mapView, sites);
        })
        .catch((error: unknown) => {
          console.warn(
            '[EPA Helene ERMA] Failed to seed in-cone NPL Superfund layer.',
            error
          );
        });
    } else if (demoKind === 'dhs') {
      removeLayerById(mapView, HISTORICAL_HURRICANES_LAYER_ID);
      removeLayerById(mapView, CFA_HELENE_STORES_LAYER_ID);
      removeLayerById(mapView, EPA_GULF_FACILITIES_LAYER_ID);
      removeLayerById(mapView, ONTARIO_GTA_ASSETS_LAYER_ID);
      removeLayerById(mapView, CNIC_HAWAII_ASSETS_LAYER_ID);
      removeLayerById(mapView, EPA_ERMA_NPL_LAYER_ID);
      ensureEpaErmaHeleneLayers(mapView);

      const dhsLayer = ensureLayer(
        mapView,
        DHS_REGION4_ASSETS_LAYER_ID,
        DHS_REGION4_ASSETS_LAYER_TITLE
      );
      dhsLayer.removeAll();
      dhsLayer.addMany(buildDhsAssetGraphics());
    } else if (demoKind === 'cnic') {
      removeLayerById(mapView, HISTORICAL_HURRICANES_LAYER_ID);
      removeLayerById(mapView, CFA_HELENE_STORES_LAYER_ID);
      removeLayerById(mapView, EPA_GULF_FACILITIES_LAYER_ID);
      removeLayerById(mapView, DHS_REGION4_ASSETS_LAYER_ID);
      removeLayerById(mapView, ONTARIO_GTA_ASSETS_LAYER_ID);
      removeLayerById(mapView, EPA_ERMA_NPL_LAYER_ID);
      removeEpaErmaHeleneLayers(mapView);

      const cnicLayer = ensureLayer(
        mapView,
        CNIC_HAWAII_ASSETS_LAYER_ID,
        CNIC_HAWAII_ASSETS_LAYER_TITLE
      );
      cnicLayer.removeAll();
      cnicLayer.addMany(buildCnicAssetGraphics());
    } else {
      removeLayerById(mapView, HISTORICAL_HURRICANES_LAYER_ID);
      removeLayerById(mapView, EPA_GULF_FACILITIES_LAYER_ID);
      removeLayerById(mapView, DHS_REGION4_ASSETS_LAYER_ID);
      removeLayerById(mapView, ONTARIO_GTA_ASSETS_LAYER_ID);
      removeLayerById(mapView, CNIC_HAWAII_ASSETS_LAYER_ID);
      removeLayerById(mapView, EPA_ERMA_NPL_LAYER_ID);
      ensureEpaErmaHeleneLayers(mapView);

      const storesLayer = ensureLayer(
        mapView,
        CFA_HELENE_STORES_LAYER_ID,
        CFA_HELENE_STORES_LAYER_TITLE
      );
      storesLayer.removeAll();
      storesLayer.addMany(buildStoreGraphics());
    }

    seededRef.current = true;

    return () => {
      cancelled = true;
      // Keep layers while flag stays on across remounts; cleanup when map tears down.
      if (!mapView.map) return;
    };
  }, [mapView, demoKind, focEnabled]);
}
